import {
    Box,
    Flex,
    Text,
    Select,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Spinner,
    Center,
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useGetDeviceGroupsForPeriodQuery } from '../../reports/api/reportsApi'
import { getDateRange } from "../../../utils/helpers";
import consumatorIcon from "../../../assets/locations/consumatorIcon.png"
import generatorIcon from "../../../assets/locations/generatorsIcon.png"

const period = [
    { id: 1, name: "1 day" },
    { id: 2, name: "1 week" },
    { id: 3, name: "1 month" },
    { id: 4, name: "1 year" },
];

export function DeviceGroups() {
    const [selectedPeriod, setSelectedPeriod] = useState(period[0]);
    const { from, to } = getDateRange(selectedPeriod.name);

    const {
      data: groups,
      isLoading,
      error,
    } = useGetDeviceGroupsForPeriodQuery({ from_date: from, to_date: to })

    const consumators = groups?.filter((item) => item.consumption_value > 0);
    const generators = groups?.filter((item) => item.generated_value > 0);

    return (
        <Box
            bg="white"
            p={4}
            borderRadius="xl"
            boxShadow="sm"
            fontFamily="Inter"
        >
            {/* Header */}
            <Flex justify="space-between" align="center" mb={4}>
                <Text fontWeight={700} fontSize="18px" color="#484848">
                    Device groups
                </Text>
                <Select
                    value={selectedPeriod.name}
                    onChange={(e) =>
                        setSelectedPeriod(
                            period.find((loc) => loc.name === e.target.value) || period[0]
                        )
                    }
                    size="sm"
                    maxW="160px"
                    fontSize="sm"
                    variant="outline"
                >
                    {period.map((x) => (
                        <option key={x.id} value={x.name}>
                            {x.name}
                        </option>
                    ))}
                </Select>
            </Flex>

            {/* Tabs */}
            <Tabs variant="unstyled">
                {(!isLoading) && (
                    <TabList bg="transparent" gap={2} mb={4}>
                        <Tab
                            fontSize="14px"
                            fontWeight="700"
                            px={4}
                            py={2}
                            borderRadius="full"
                            color="#4B4B4B"
                            _selected={{
                                bg: "#F3F3F3",
                                fontWeight: "700",
                                color: "#1A1A1A",
                            }}
                        >
                            Consumators
                        </Tab>
                        <Tab
                            fontSize="14px"
                            fontWeight="700"
                            px={4}
                            py={2}
                            borderRadius="full"
                            color="#4B4B4B"
                            _selected={{
                                bg: "#F3F3F3",
                                fontWeight: "700",
                                color: "#1A1A1A",
                            }}
                        >
                            Generators
                        </Tab>
                    </TabList>
                )}

                <TabPanels>
                    <TabPanel p={0}>
                        {isLoading ? (
                            <Center py={10}>
                                <Spinner size="md" />
                            </Center>
                        ) : error ? (
                            <Alert
                                status="info"
                                bg="blue.50"
                                borderRadius="md"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                color="blue.800"
                            >
                                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                                <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                                    No data
                                </AlertTitle>
                                <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                                    No data available for the selected period
                                </AlertDescription>
                            </Alert>
                        ) : groups?.length === 0 ? (
                            <Alert
                                status="info"
                                bg="blue.50"
                                borderRadius="md"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                color="blue.800"
                                py={6}
                            >
                                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                                <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                                    No smart devices
                                </AlertTitle>
                                <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                                    Currently there are no devices that support automation and smart control
                                </AlertDescription>
                            </Alert>
                        ) : consumators?.length === 0 ? (
                            <Alert
                                status="info"
                                bg="blue.50"
                                borderRadius="md"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                color="blue.800"
                                py={6}
                            >
                                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                                <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                                    No consumators
                                </AlertTitle>
                                <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                                    Currently there are no consumators available
                                </AlertDescription>
                            </Alert>
                        ) : (
                            consumators?.map((device, idx) => (
                                <Flex
                                    key={idx}
                                    align="center"
                                    justify="space-between"
                                    p={3}
                                    borderRadius="lg"
                                    bg="#f5f6f7"
                                    mb={2}
                                >
                                    <Flex align="center" gap={3}>
                                        <Box
                                            bg="gray.100"
                                            boxSize="36px"
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <img
                                                src={device.icon || consumatorIcon}
                                                alt={device?.title}
                                                style={{
                                                    width: "50px",
                                                    height: "50px",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <Text fontWeight={400} fontSize="14px" color="#484848">
                                                {device?.title}
                                            </Text>
                                        </Box>
                                    </Flex>
                                    <Text fontWeight={700} fontSize="14px" color="#484848">
                                        {device?.consumption_value.toFixed(2)} <span style={{ color: "#9B9B9B" }}>kW</span>
                                    </Text>
                                </Flex>
                            ))
                        )}
                    </TabPanel>

                    <TabPanel p={0}>
                        {generators?.length === 0 ? (
                            <Alert
                                status="info"
                                bg="blue.50"
                                borderRadius="md"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                color="blue.800"
                                py={6}
                            >
                                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                                <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                                    No generators
                                </AlertTitle>
                                <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                                    Currently there are no generators available
                                </AlertDescription>
                            </Alert>
                        ) : (
                            generators?.map((device, idx) => (
                                <Flex
                                    key={idx}
                                    align="center"
                                    justify="space-between"
                                    p={3}
                                    borderRadius="lg"
                                    bg="#f5f6f7"
                                    mb={2}
                                >
                                    <Flex align="center" gap={3}>
                                        <Box
                                            bg="gray.100"
                                            boxSize="36px"
                                            borderRadius="full"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <img
                                                src={device.icon || generatorIcon}
                                                alt={device?.title}
                                                style={{
                                                    width: "50px",
                                                    height: "50px",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <Text fontWeight={400} fontSize="14px" color="#484848">
                                                {device?.title}
                                            </Text>
                                        </Box>
                                    </Flex>
                                    <Text fontWeight={700} fontSize="14px" color="#484848">
                                        {device?.generated_value.toFixed(2)} <span style={{ color: "#9B9B9B" }}>kW</span>
                                    </Text>
                                </Flex>
                            ))
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs >
        </Box >
    );
}
